import Parameters from 'dc-engine/src/parameters';
import TargetFundSolver from 'dc-engine/src/lib/targetFundSolver';
import {
  incomeTax,
  AdvDate,
  dateUtils,
  statePensionDate
} from '@lifetime-gallagher/calculator-toolkit';
import _ from "lodash";

const convertStatusToLetter = function(status) {
  return _.get({
    0: '',
    1: 'A',
    2: 'B',
    3: 'C',
  }, status);
};

export default {
  state: {
    isCohabiting: null,
    isInLondon: false //null,
  },
  getters: {
    Config: (state, getters, rootState, rootGetters) => rootState.config,

    age: (state, getters) => getters.Config.inputs.age.currentValue,
    dateOfBirth: (state, getters) => {
      const today = dateUtils.today();
      return new AdvDate(today.getFullYear() - getters.age, today.getMonth(), today.getDate());
    },
    retirementAge: (state, getters) => Math.max(getters.Config.inputs.retirementAge.currentValue, getters.minRetirementAge),
    currentBalance: (state, getters) => getters.Config.inputs.currentBalance.currentValue,
    annuityRate: (state, getters) => getters.Config.engine.annuityRate,
    inflation: (state, getters) => getters.Config.model.discountRate,

    // State Pension is doubled for couples
    statePension: (state, getters) =>
      getters.Config.engine.statePension * (state.isCohabiting ? 2 : 1),
    statePensionDate: (state, getters) => statePensionDate('MALE', getters.dateOfBirth),
    statePensionAge: (state, getters) => Math.ceil(dateUtils.monthsBetween(getters.dateOfBirth, getters.statePensionDate) / 12),
    totalPeriodYrs: (state, getters) => getters.retirementAge - getters.age,
    resultPerMonth: (state, getters) => getters.solvedTargetYearlyConts / 12,

    locationKey: state => (state.isInLondon ? "london" : "notLondon"),
    cohabitingKey: state => (state.isCohabiting ? "cohabiting" : "single"),

    targetIncome: (state, getters) => {
      if (getters.areInputsValid === false) {
        return 0;
      }

      const keys = Object.keys(getters.answers);

      let totalValue = 0;
      keys.forEach(key => {
        const answerLetter = convertStatusToLetter(getters.answers[key]);
        totalValue += getters.Config.targetIncomes[getters.cohabitingKey][key][answerLetter];
      });

      return totalValue * 52;
    },

    incomeIsCoveredByStatePension: (state, getters) => {
      return getters.grossIncomeNeeded < 0;
    },

    isOnTrack: (state, getters) => {
      return getters.resultPerMonth === 0;
    },

    maxValueForStandard: (state, getters) => standardLetterKey => {
      const keys = Object.keys(getters.answers);
      let totalValue = 0;

      keys.forEach(key => {
        totalValue += getters.Config.targetIncomes[getters.cohabitingKey][key][standardLetterKey];
      });

      return totalValue * 52;
    },

    standards: (state, getters) => {
      return {
        minimum: {
          text: "minimum",
          maxValue: getters.maxValueForStandard("A")
        },
        moderate: {
          text: "moderate",
          maxValue: getters.maxValueForStandard("B")
        },
        comfortable: {
          text: "comfortable",
          maxValue: getters.maxValueForStandard("C")
        }
      };
    },

    standardChosen: (state, getters) => {
      const { standards, targetIncome } = getters;

      if (targetIncome <= standards.minimum.maxValue) {
        return standards.minimum.text;
      } else if (targetIncome <= standards.moderate.maxValue) {
        return standards.moderate.text;
      } else if (targetIncome <= standards.comfortable.maxValue) {
        return standards.comfortable.text;
      }
    },

    /*
      target fund is based on gross income required to cover expenditure requirement minus State Pension
      then using an annuity rate of 20 to convert to desired fund
    */
    nominalFundAtRet: (state, getters) => {
      return (
        getters.grossIncomeNeeded *
        Math.pow(1 + getters.inflation, getters.totalPeriodYrs) *
        getters.annuityRate
      );
    },

    /*
      for couples we assume each person will contribute half the income.
      Tax is calculated for half the expenditure target, then the gross income doubled to determine the final fund value
    */
    grossIncomeNeeded: (state, getters) => {
      const MAX_ITERATIONS = 40;
      const targetNet = getters.targetIncome / (state.isCohabiting ? 2 : 1);
      const firstGuess = targetNet * 2;
      const financialYear = new AdvDate().financialYearString();
      let stepSize = firstGuess / 2;
      let income = firstGuess;
      let currentNet;

      for (let i = 0; i < MAX_ITERATIONS; i++) {
        currentNet = income - incomeTax.annualPayable(income, financialYear).total;
        if (targetNet > currentNet) {
          income += stepSize;
        } else if (targetNet < currentNet) {
          income -= stepSize;
        } else {
          break;
        }
        stepSize /= 2;
      }
      return income * (state.isCohabiting ? 2 : 1) - getters.statePension;
    },

    isAgeValid: (state, getters) =>
      _.inRange(getters.age, getters.Config.inputs.age.min, getters.Config.inputs.age.max),
    minRetirementAge: (state, getters) =>
      Math.max(getters.age + 1, getters.Config.canChangeRetirementAge ? getters.Config.inputs.retirementAge.min : getters.statePensionAge),
    isRetirementAgeValid: (state, getters) => {
      return _.inRange(
        getters.retirementAge,
        getters.minRetirementAge,
        getters.Config.inputs.retirementAge.max
      );
    },
    isCurrentBalanceValid: (state, getters) =>
      _.inRange(
        getters.currentBalance,
        getters.Config.inputs.currentBalance.min,
        getters.Config.inputs.currentBalance.max
      ),
    isCohabitingValid: state => _.isNull(state.isCohabiting, state.isInLondon) === false,

    areInputsValid: (state, getters) => {
      return _.every([
        getters.isAgeValid,
        getters.isRetirementAgeValid,
        getters.isCurrentBalanceValid,
        getters.isCohabitingValid
      ]);
    },

    rawDcEngineParameters: (state, getters) => {
      return _.defaultsDeep(
        {
          person: {
            age: getters.age,
            currentFund: getters.currentBalance,
            retirementAge: getters.retirementAge
          },
          targetFundSolver: {
            targetFundAtRetirement: getters.nominalFundAtRet
          }
        },
        getters.Config
      );
    },

    dcEngineParameters: (state, getters) => {
      if (getters.areInputsValid === false) {
        return null;
      }
      return new Parameters(getters.rawDcEngineParameters);
    },

    solvedTargetYearlyConts: (state, getters) => {
      if (getters.areInputsValid === false) {
        return null;
      }

      const parameters = getters.dcEngineParameters;

      const targetFundSolver = new TargetFundSolver(parameters);
      targetFundSolver.solve(parameters.targetFundSolver.targetFundAtRetirement);

      return targetFundSolver.solvedEESavingConts;
    }
  },
  mutations: {
    selectIsCohabiting(state, isCohabiting) {
      state.isCohabiting = isCohabiting;
    },

    selectIsInLondon(state, isInLondon) {
      state.isInLondon = isInLondon;
    }
  }
};
